import { OpenAPIObject, SchemaObject } from 'openapi3-ts';
import { BaseProps, SchemaObjectEntry } from '.';
import { ClientPlatform } from '../types';
import { replaceApiRouteLinks, snarkdownWithParagraphs } from '../utilities';
import snarkdown from 'snarkdown';
import styles from './index.module.scss';
import SchemaLayout from './SchemaLayout/SchemaLayout';
import { normalizeSchemaForRendering } from './SchemaLayout/schemaUtils';

interface LinkSchemaInternalProps extends BaseProps {
  definition: OpenAPIObject;
  sdk: ClientPlatform;
  methodName: string;
  simplifyForBill: boolean;
}

const generateLinkSchema = (
  methodName: string,
  definition: OpenAPIObject,
  sdk: ClientPlatform,
) => {
  const linkSchema: SchemaObject =
    definition.components.schemas[`${sdk}.${methodName}`];
  if (linkSchema == null) {
    throw new Error(
      'Schema: props.sdk & props.method must be available in link.yml',
    );
  }
  return linkSchema;
};

// LinkSchema is used to display the schema for the Link SDK methods
// (For example, the schemas at https://plaid.com/docs/link/web/)
const LinkSchema: React.FC<LinkSchemaInternalProps> = (props) => {
  // Normalized like every other schema the table renders.
  const linkSchema: SchemaObject = normalizeSchemaForRendering(
    generateLinkSchema(props.methodName, props.definition, props.sdk),
  );
  const methodKebabCase = props.methodName.replace('/', '-').toLowerCase();

  // Some link models don't have any properties, but still contain
  // a description and example. For example web.open() and web.destroy().
  const entries: Array<SchemaObjectEntry> = Object.entries(
    linkSchema.properties || {},
  );
  return (
    <SchemaLayout
      route={props.methodName}
      schemaName={`link-${props.sdk}-${methodKebabCase}`}
      tableOnly={props.tableOnly}
      simplify={props.simplifyForBill}
      expandAll={!props.collapse}
      expandText={props.expandText}
      hideText={props.hideText}
      unhideFlag={props.unhideFlag}
      entries={entries}
      schema={linkSchema}
      required={linkSchema.required}
      type='link'
      description={
        <div
          className={styles.description}
          dangerouslySetInnerHTML={{
            __html: snarkdownWithParagraphs(
              snarkdown,
              replaceApiRouteLinks(
                linkSchema.description || '',
                props.definition,
              ),
            ),
          }}
        />
      }
      headerText='Properties'
    >
      {props.children}
    </SchemaLayout>
  );
};

export default LinkSchema;
